import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { AppService } from '@app/app.service';
import { IButtonExtended } from '@components/button/interfaces/button.interface';
import { FuiButtonComponent } from '@fui/fui-components';
import { FuiDestroyService } from '@services/destroy.service';
import { BehaviorSubject, debounceTime, takeUntil } from 'rxjs';

/**
 * Универсальный компонент сайдбара.
 * В него можно встраивать любой другой контент.
 */
@Component({
    selector: 'fui-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    imports: [CommonModule, FuiButtonComponent, MatSidenavModule],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FuiSidebarComponent implements OnInit {
    public readonly appService = inject(AppService);

    readonly #cdr = inject(ChangeDetectorRef);

    readonly #destroy$ = inject(FuiDestroyService);

    /** Событие изменения состояния сайдбара: открыт/закрыт */
    visible$ = new BehaviorSubject<boolean>(false);

    /**
     * Открыть/закрыть сайдбар извне и сбросить кнопки закрываемой панели.
     * @param value Открыть панель при true; закрыть при false.
     */
    @Input() set visible(value: boolean) {
        if (!value) {
            this.headerButtons$.next([]);
        }

        this.visible$.next(value);
    }

    /** Заголовок сайдбара */
    @Input() title = '';

    /** Кнопки шапки, предоставляемые содержимым панели на время её открытия. */
    readonly headerButtons$ = new BehaviorSubject<IButtonExtended[]>([]);

    /** Имя действия нажатой кнопки шапки из её поля click. */
    @Output() headerButtonClick = new EventEmitter<string>();

    /** Дополнительные CSS классы для сайдбара */
    @Input() matSideNavCss = '';

    /** Выбрасываем наружу состояние открыт/закрыт, если оно было изменено изнутри компонента */
    @Output() openedChange = new EventEmitter<boolean>();

    /** Компонент MatSidenav */
    @ViewChild('matSideNav') matSideNav?: MatSidenav;

    /** Инициализация компонента */
    ngOnInit(): void {
        this.visible$
            .pipe(debounceTime(1), takeUntil(this.#destroy$))
            .subscribe((visible) => this.toggleSideNav(visible));
    }

    /** Отдать команду закрыть сайдбар */
    async closeSideNav() {
        if (this.matSideNav) {
            await this.matSideNav.close();
            this.openedChange.emit(false);
        }
    }

    /** Открыть/закрыть сайдбар по команде извне */
    private toggleSideNav(visible: boolean) {
        if (this.matSideNav) {
            this.matSideNav.toggle(visible);
            this.#cdr.markForCheck();
        }
    }
}
